import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Download as DownloadIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import TableActionButton from "../../components/layout/TableActionButton";
import supplierAPI, { Supplier, SupplierPayload } from "../../services/supplierAPI";
import reportAPI from "../../services/reportAPI";
import { rowsToCsv } from "../../utils/csvExport";
import { useRBAC } from "../../hooks/useRBAC";

const emptyForm = (): SupplierPayload => ({
  name: "",
  contactPerson: "",
  phone: "",
  email: "",
  taxId: "",
  city: "",
  state: "",
  address: "",
  isActive: true,
});

const SuppliersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete, canRead } = useRBAC();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState<SupplierPayload>(emptyForm());
  const [exporting, setExporting] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["suppliers", page, rowsPerPage, search],
    queryFn: () =>
      supplierAPI.getSuppliers({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        search: search || undefined,
      }),
  });

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: async () => {
      if (editing) {
        return supplierAPI.updateSupplier(editing.id, form);
      }
      return supplierAPI.createSupplier(form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["suppliersAll"] });
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      setAlert({
        type: "success",
        message: editing ? "Supplier updated." : "Supplier created.",
      });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to save supplier",
      });
    },
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: string) => supplierAPI.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["suppliersAll"] });
      setAlert({ type: "success", message: "Supplier deleted." });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete supplier",
      });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row: Supplier) => {
    setEditing(row);
    setForm({
      name: row.name,
      contactPerson: row.contactPerson || "",
      phone: row.phone || "",
      email: row.email || "",
      taxId: row.taxId || "",
      city: row.city || "",
      state: row.state || "",
      address: row.address || "",
      isActive: row.isActive,
    });
    setOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      setAlert({ type: "error", message: "Name is required." });
      return;
    }
    save();
  };

  async function handleTallyExport() {
    setExporting(true);
    try {
      const data = await reportAPI.getTallyPartiesExport();
      const csv = rowsToCsv(data.rows || []);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tally_parties.csv";
      link.click();
      URL.revokeObjectURL(url);
      setAlert({ type: "success", message: "Tally party master CSV downloaded." });
    } catch (err: any) {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to export parties",
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <Box>
      <PageHeader
        title="Suppliers"
        subtitle="Party master for purchases, debit notes and ledgers"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Suppliers" },
        ]}
        action={
          canCreate("purchases") || canRead("reports") ? (
            <Stack direction="row" spacing={1}>
              {canRead("reports") && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={exporting}
                  onClick={() => void handleTallyExport()}
                >
                  Tally export
                </Button>
              )}
              {canCreate("purchases") ? (
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                  Add supplier
                </Button>
              ) : null}
            </Stack>
          ) : undefined
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || "Failed to load suppliers"}
        </Alert>
      )}

      <DataListCard
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search name, phone, GSTIN…"
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>GSTIN / Tax ID</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              ) : (data?.data || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No suppliers yet.</TableCell>
                </TableRow>
              ) : (
                (data?.data || []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.phone || "—"}</TableCell>
                    <TableCell>{row.taxId || "—"}</TableCell>
                    <TableCell>{row.city || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.isActive ? "Active" : "Inactive"}
                        color={row.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canUpdate("purchases") && (
                          <TableActionButton
                            title="Edit"
                            variant="primary"
                            onClick={() => openEdit(row)}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canDelete("purchases") && (
                          <TableActionButton
                            title="Delete"
                            variant="danger"
                            onClick={() => {
                              if (window.confirm(`Delete supplier "${row.name}"?`)) {
                                remove(row.id);
                              }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.total || 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </DataListCard>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit supplier" : "Add supplier"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact person"
                value={form.contactPerson || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, contactPerson: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone || ""}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={form.email || ""}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GSTIN / Tax ID"
                value={form.taxId || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, taxId: e.target.value.toUpperCase() }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={form.city || ""}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={form.state || ""}
                onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                minRows={2}
                value={form.address || ""}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive !== false}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, isActive: e.target.checked }))
                    }
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuppliersPage;
